/* eslint-disable @typescript-eslint/no-explicit-any */
import { Res } from '@cbo/shared-library';
import { GridAggregationModel, GridColDef, GridColTypeDef } from '@mui/x-data-grid-premium';
import {
  activeColDef,
  businessDateColDef,
  dateColDef,
  getCurrencyColDef,
  hoursColDef,
  humanReadableTimeDurationColDef,
  integerColDef,
  percentageColDef,
  timezoneAwareDateColDef,
} from '../../utils/reportingUtils/datagridUtils';
import { formatCurrency, getCurrencyFormat } from '../../utils/formatters';
import { CurrencyFormat, IsoCurrency } from '../../models/config';
import { BaseDataset, CheckSummaryColumns, ItemDiscountsColumns, ItemSalesColumns, LaborShiftsColumns } from './enums';
import { ColumnDefinition, CustomReportData, DatagridElement } from '../requests/index';

export enum DatagridColumnTypes {
  Integer,
  Hours,
  TimeDuration,
  Percentage,
  DateTime,
  Date,
  Currency,
  Boolean,
}

export const NonDraggableSelectors =
  '.MuiBackdrop-root, .MuiInputBase-input, .MuiTypography-root, .MuiDialogContent-root, [aria-roledescription="draggable"]';

export interface ColumnConfig {
  type?: DatagridColumnTypes;
  /** Optional explicit display label. Falls back to PascalCase splitting when absent. */
  headerName?: string;
}

type ColumnTypeMapping = {
  [BaseDataset.CheckSummary]: Partial<Record<CheckSummaryColumns, ColumnConfig | undefined>>;
  [BaseDataset.ItemSales]: Partial<Record<ItemSalesColumns, ColumnConfig | undefined>>;
  [BaseDataset.ItemDiscounts]: Partial<Record<ItemDiscountsColumns, ColumnConfig | undefined>>;
  [BaseDataset.LaborShifts]: Partial<Record<LaborShiftsColumns, ColumnConfig | undefined>>;
};

// `undefined` means the column's type isn't important to us - it doesn't affect
// how this column is drawn in the datagrid. In other words, we don't need to
// add additional info to its corresponding MUI datagrid column definition.
const columnTypeMapping: ColumnTypeMapping = {
  [BaseDataset.CheckSummary]: {
    [CheckSummaryColumns.BusinessDate]: { type: DatagridColumnTypes.Date, headerName: 'Business date' },
    [CheckSummaryColumns.StoreId]: { headerName: 'Site ID' },
    [CheckSummaryColumns.StoreName]: { headerName: 'Site name' },
    [CheckSummaryColumns.TransactionNumber]: { headerName: 'Transaction #' },
    [CheckSummaryColumns.TableDescription]: { headerName: 'Table name' },
    [CheckSummaryColumns.CompAmount]: { type: DatagridColumnTypes.Currency, headerName: 'Comp amount' },
    [CheckSummaryColumns.PromoAmount]: { type: DatagridColumnTypes.Currency, headerName: 'Promo amount' },
    [CheckSummaryColumns.PaymentAmount]: { type: DatagridColumnTypes.Currency },
    [CheckSummaryColumns.CashTenders]: { type: DatagridColumnTypes.Currency, headerName: 'Cash tenders amount' },
    [CheckSummaryColumns.NonCashTenders]: { type: DatagridColumnTypes.Currency, headerName: 'Non cash tenders amount' },
    [CheckSummaryColumns.NonCashTips]: { type: DatagridColumnTypes.Currency, headerName: 'Non cash tips amount' },
    [CheckSummaryColumns.Gratuities]: { type: DatagridColumnTypes.Currency, headerName: 'Gratuities amount' },
    [CheckSummaryColumns.GuestCount]: { type: DatagridColumnTypes.Integer, headerName: 'Guest count' },
    [CheckSummaryColumns.ItemCount]: { type: DatagridColumnTypes.Integer, headerName: 'Item count' },
    [CheckSummaryColumns.ItemSales]: { type: DatagridColumnTypes.Currency, headerName: 'Item sales amount' },
    [CheckSummaryColumns.GrossSales]: { type: DatagridColumnTypes.Currency, headerName: 'Gross sales amount' },
    [CheckSummaryColumns.GiftCardSales]: { type: DatagridColumnTypes.Currency, headerName: 'Gift card sales amount' },
    [CheckSummaryColumns.SurchargeAmount]: { type: DatagridColumnTypes.Currency, headerName: 'Surcharge amount' },
    [CheckSummaryColumns.ExclusiveTaxes]: { type: DatagridColumnTypes.Currency, headerName: 'Exclusive taxes amount' },
    [CheckSummaryColumns.InclusiveTaxes]: { type: DatagridColumnTypes.Currency, headerName: 'Inclusive taxes amount' },
    [CheckSummaryColumns.DayPart]: { headerName: 'Day part' },
    [CheckSummaryColumns.RevenueCenter]: { headerName: 'Revenue center' },
    [CheckSummaryColumns.NetSales]: { type: DatagridColumnTypes.Currency, headerName: 'Net sales amount' },
    [CheckSummaryColumns.TaxExemptAmount]: { type: DatagridColumnTypes.Currency, headerName: 'Tax exempt amount' },
    [CheckSummaryColumns.TimeZone]: { headerName: 'Time zone' },
    [CheckSummaryColumns.EmployeeId]: { headerName: 'Employee ID' },
    [CheckSummaryColumns.EmployeeName]: { headerName: 'Employee name' },
    [CheckSummaryColumns.CheckOpenTime]: { type: DatagridColumnTypes.DateTime, headerName: 'Open time' },
    [CheckSummaryColumns.CheckCloseTime]: { type: DatagridColumnTypes.DateTime, headerName: 'Close time' },
    [CheckSummaryColumns.VoidAmount]: { type: DatagridColumnTypes.Currency, headerName: 'Void amount' },
  },
  [BaseDataset.ItemSales]: {
    [ItemSalesColumns.BusinessDate]: { type: DatagridColumnTypes.Date, headerName: 'Business date' },
    [ItemSalesColumns.StoreId]: { headerName: 'Site ID' },
    [ItemSalesColumns.StoreName]: { headerName: 'Site name' },
    [ItemSalesColumns.TransactionNumber]: { headerName: 'Transaction #' },
    [ItemSalesColumns.EmployeeId]: { headerName: 'Employee ID' },
    [ItemSalesColumns.EmployeeName]: { headerName: 'Employee name' },
    [ItemSalesColumns.ProductId]: { headerName: 'Product ID' },
    [ItemSalesColumns.ProductName]: { headerName: 'Product name' },
    [ItemSalesColumns.ItemId]: { headerName: 'Item ID' },
    [ItemSalesColumns.ParentItemId]: { headerName: 'Parent Item ID' },
    [ItemSalesColumns.RevenueCenterId]: { headerName: 'Revenue center ID' },
    [ItemSalesColumns.RevenueCenterName]: { headerName: 'Revenue center Name' },
    [ItemSalesColumns.CategoryId]: { headerName: 'Category ID' },
    [ItemSalesColumns.CategoryName]: { headerName: 'Category name' },
    [ItemSalesColumns.OrderModeId]: { headerName: 'Order mode ID' },
    [ItemSalesColumns.OrderModeName]: { headerName: 'Order mode name' },
    [ItemSalesColumns.ItemPrice]: { type: DatagridColumnTypes.Currency, headerName: 'Item price' },
    [ItemSalesColumns.DiscountedPrice]: { type: DatagridColumnTypes.Currency, headerName: 'Item discounted price' },
    [ItemSalesColumns.Quantity]: { headerName: 'Item quantity' },
    [ItemSalesColumns.AddedDateTime]: { type: DatagridColumnTypes.DateTime, headerName: 'Item added time' },
    [ItemSalesColumns.SellType]: { headerName: 'Item sale type' },
    [ItemSalesColumns.IsReturned]: { type: DatagridColumnTypes.Boolean, headerName: 'Item is returned' },
    [ItemSalesColumns.IsVoided]: { type: DatagridColumnTypes.Boolean, headerName: 'Item is voided' },
    [ItemSalesColumns.TimeZone]: { headerName: 'Time zone' },
  },
  [BaseDataset.ItemDiscounts]: {
    [ItemDiscountsColumns.BusinessDate]: { type: DatagridColumnTypes.Date, headerName: 'Business date' },
    [ItemDiscountsColumns.StoreId]: { headerName: 'Site ID' },
    [ItemDiscountsColumns.StoreName]: { headerName: 'Site name' },
    [ItemDiscountsColumns.TransactionNumber]: { headerName: 'Transaction #' },
    [ItemDiscountsColumns.ProductId]: { headerName: 'Product ID' },
    [ItemDiscountsColumns.ProductName]: { headerName: 'Product name' },
    [ItemDiscountsColumns.ItemId]: { headerName: 'Item ID' },
    [ItemDiscountsColumns.DiscountType]: { headerName: 'Discount type' },
    [ItemDiscountsColumns.UniqueId]: { headerName: 'Discount unique ID' },
    [ItemDiscountsColumns.Id]: { headerName: 'Discount ID' },
    [ItemDiscountsColumns.Name]: { headerName: 'Discount name' },
    [ItemDiscountsColumns.ItemPrice]: { type: DatagridColumnTypes.Currency, headerName: 'Item price' },
    [ItemDiscountsColumns.DiscountedPrice]: { type: DatagridColumnTypes.Currency, headerName: 'Item discounted price' },
    [ItemDiscountsColumns.DiscountAmount]: { type: DatagridColumnTypes.Currency, headerName: 'Item discounted amount' },
    [ItemDiscountsColumns.AddedDateTime]: { type: DatagridColumnTypes.DateTime, headerName: 'Discount added time' },
    [ItemDiscountsColumns.TimeZone]: { headerName: 'Time zone' },
  },
  [BaseDataset.LaborShifts]: {
    [LaborShiftsColumns.BusinessDate]: { type: DatagridColumnTypes.Date, headerName: 'Business Day' },
    [LaborShiftsColumns.StoreId]: { headerName: 'Site ID' },
    [LaborShiftsColumns.StoreName]: { headerName: 'Site name' },
    [LaborShiftsColumns.EmployeeId]: { headerName: 'Employee ID' },
    [LaborShiftsColumns.EmployeeName]: { headerName: 'Employee name' },
    [LaborShiftsColumns.ShiftId]: { headerName: 'Shift ID' },
    [LaborShiftsColumns.JobId]: { headerName: 'Job ID' },
    [LaborShiftsColumns.JobName]: { headerName: 'Job name' },
    [LaborShiftsColumns.ClockInDatetime]: { type: DatagridColumnTypes.DateTime, headerName: 'Clock in time' },
    [LaborShiftsColumns.ClockOutDatetime]: { type: DatagridColumnTypes.DateTime, headerName: 'Clock out time' },
    [LaborShiftsColumns.TotalShiftTime]: { type: DatagridColumnTypes.TimeDuration, headerName: 'Total shift time' },
    [LaborShiftsColumns.TotalPaidBreakTime]: {
      type: DatagridColumnTypes.TimeDuration,
      headerName: 'Total paid break time',
    },
    [LaborShiftsColumns.TotalUnpaidBreakTime]: {
      type: DatagridColumnTypes.TimeDuration,
      headerName: 'Total unpaid break time',
    },
    [LaborShiftsColumns.TotalWages]: { type: DatagridColumnTypes.Currency, headerName: 'Total wages' },
    [LaborShiftsColumns.DeclaredTips]: { type: DatagridColumnTypes.Currency, headerName: 'Declared tips' },
    [LaborShiftsColumns.EmployeeSales]: { type: DatagridColumnTypes.Currency, headerName: 'Employee sales' },
    [LaborShiftsColumns.Edited]: { type: DatagridColumnTypes.Boolean, headerName: 'Shift is edited' },
    [LaborShiftsColumns.LastEditedDateTime]: { type: DatagridColumnTypes.DateTime, headerName: 'Last edited time' },
    [LaborShiftsColumns.LastEditedById]: { headerName: 'Last edited by ID' },
    [LaborShiftsColumns.LastEditedByName]: { headerName: 'Last edited by Name' },
    [LaborShiftsColumns.TimeZone]: { headerName: 'Time zone' },
  },
};

// This lets us interface with the columnTypeMapping hard coded mapping
// without the TypeScript shenanigans.
function getColumnType(baseDataset: string, columnName: string): DatagridColumnTypes | undefined {
  const dataset = baseDataset as BaseDataset;
  const datasetMapping = columnTypeMapping[dataset] as Record<string, ColumnConfig | undefined>;
  return datasetMapping[columnName]?.type;
}

export function getColumnConfig(baseDataset: string, columnName: string): ColumnConfig | undefined {
  const dataset = baseDataset as BaseDataset;
  const datasetMapping = columnTypeMapping[dataset];
  return datasetMapping?.[columnName as keyof typeof datasetMapping];
}

function buildGridColDefFieldName(baseDataset: string, columnName: string): string {
  return `${baseDataset}_${columnName}`;
}

function getDecimalPlacesFromCustomMetricFormat(format?: string): number | undefined {
  if (!format) return undefined;

  const normalizedFormat = format.trim().toLowerCase();

  const isNearest100 =
    normalizedFormat === 'decimaltonearest100' ||
    normalizedFormat === 'hundredth' ||
    normalizedFormat.includes('nearest100') ||
    normalizedFormat.includes('100th') ||
    normalizedFormat === '0.00' ||
    normalizedFormat === '$0.00' ||
    normalizedFormat === '0.00%';

  const isNearest10 =
    normalizedFormat === 'decimaltonearest10' ||
    normalizedFormat === 'tenth' ||
    normalizedFormat.includes('nearest10') ||
    normalizedFormat.includes('10th') ||
    normalizedFormat === '0.0' ||
    normalizedFormat === '$0.0' ||
    normalizedFormat === '0.0%';

  // Evaluate nearest100 first so "nearest100" is never treated as "nearest10".
  if (isNearest100) {
    return 2;
  }

  // Support API enum values, legacy display values, and human-friendly variants.
  if (isNearest10) {
    return 1;
  }

  return undefined;
}

function formatNumberWithDecimals(value: number, decimals: number): string {
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function getTimezoneAwareFormattingIfRequired(baseDataset: string, columnName: string): GridColTypeDef | null {
  if (
    baseDataset === BaseDataset.CheckSummary &&
    (columnName === CheckSummaryColumns.CheckOpenTime || columnName === CheckSummaryColumns.CheckCloseTime)
  ) {
    return timezoneAwareDateColDef(CheckSummaryColumns.TimeZone);
  }

  if (baseDataset === BaseDataset.ItemSales && columnName === ItemSalesColumns.AddedDateTime) {
    return timezoneAwareDateColDef(ItemSalesColumns.TimeZone);
  }

  if (baseDataset === BaseDataset.ItemDiscounts && columnName === ItemDiscountsColumns.AddedDateTime) {
    return timezoneAwareDateColDef(ItemDiscountsColumns.TimeZone);
  }

  return null;
}

export function buildGridColDefs(
  detailsAndConfig: CustomReportData['detailsAndConfig'],
  preferences: Res.Admin.UserPreferences | null | undefined,
  allColumnsByDataset: Record<string, ColumnDefinition[]> = {}
): GridColDef[][] {
  // For now, single element per section only
  return detailsAndConfig.elements.map(([element]) => {
    const dataset = Object.keys(element.columns)[0];
    return element.columns[dataset].map((columnName) => {
      const columnConfig: ColumnConfig | undefined = getColumnConfig(dataset, columnName);
      const dynamicColumnMeta = allColumnsByDataset[dataset]?.find((col) => col.columnName === columnName);

      let additionalColDef;
      // TODO: It'd be great if we could memoize these somehow. We aren't
      // inside of a hook, though, so idk how that'd be possible....
      //
      // Maybe we could proactively create and memoize column defs for all the
      // possible ones in the custom report datagrid component, and then pass
      // references to each of them into this buildGridColDefs() function...?
      // We may not need all of them, but it might still be cheaper to
      // instantiate one of each instead of instantiating a brand new one each
      // time we have a column that needs one.
      //
      // Micro-optimization for sure, but worth writing down.
      switch (columnConfig?.type) {
        case DatagridColumnTypes.Integer:
          additionalColDef = integerColDef();
          break;
        case DatagridColumnTypes.Hours:
          additionalColDef = hoursColDef;
          break;
        case DatagridColumnTypes.TimeDuration:
          additionalColDef = humanReadableTimeDurationColDef;
          break;
        case DatagridColumnTypes.Percentage:
          additionalColDef = percentageColDef;
          break;
        case DatagridColumnTypes.DateTime: {
          const timezoneAwareColDef = getTimezoneAwareFormattingIfRequired(dataset, columnName);
          if (timezoneAwareColDef) {
            additionalColDef = timezoneAwareColDef;
          } else {
            additionalColDef = dateColDef;
          }
          break;
        }
        case DatagridColumnTypes.Date:
          additionalColDef = businessDateColDef(preferences);
          break;
        case DatagridColumnTypes.Currency:
          additionalColDef = getCurrencyColDef(preferences);
          break;
        case DatagridColumnTypes.Boolean:
          additionalColDef = activeColDef(columnName);
          break;
        default:
          // Custom metric columns are dynamic and not present in the static mapping above.
          // When they are numeric, provide a numeric colDef so MUI aggregation can sum them.
          if (dynamicColumnMeta?.isNumeric) {
            const normalizedMetricType = (dynamicColumnMeta.columnType ?? '').toLowerCase();
            const decimalPlaces = getDecimalPlacesFromCustomMetricFormat(dynamicColumnMeta.customMetricFormat);
            if (normalizedMetricType === 'currency') {
              additionalColDef = decimalPlaces
                ? {
                    ...getCurrencyColDef(preferences),
                    valueFormatter: (value: number) => {
                      if (value === null || value === undefined || Number.isNaN(Number(value))) {
                        return '';
                      }

                      const currencyFormat = getCurrencyFormat(preferences);
                      const currencyFormatWithPrecision: CurrencyFormat = {
                        ...currencyFormat,
                        options: {
                          ...currencyFormat.options,
                          currency: currencyFormat.options?.currency ?? IsoCurrency.US_DOLLAR,
                          minimumFractionDigits: decimalPlaces,
                        },
                      };

                      return formatCurrency(
                        Number(value),
                        preferences,
                        currencyFormatWithPrecision,
                        undefined,
                        undefined,
                        decimalPlaces
                      );
                    },
                  }
                : getCurrencyColDef(preferences);
            } else if (normalizedMetricType === 'percentage') {
              additionalColDef = decimalPlaces
                ? {
                    ...percentageColDef,
                    valueFormatter: (value: number) => {
                      if (value === null || value === undefined || Number.isNaN(Number(value))) {
                        return '';
                      }

                      return `${(Number(value) * 100).toFixed(decimalPlaces)}%`;
                    },
                  }
                : percentageColDef;
            } else {
              additionalColDef = decimalPlaces
                ? {
                    type: 'number' as const,
                    valueFormatter: (value: number) => {
                      if (value === null || value === undefined || Number.isNaN(Number(value))) {
                        return '';
                      }

                      return formatNumberWithDecimals(Number(value), decimalPlaces);
                    },
                  }
                : { type: 'number' as const };
            }
          }
          break; // Do nothing; keep additionalColDef undefined.
      }
      return {
        field: `${columnName}`,
        headerName: columnConfig?.headerName ?? `${columnName}`, // TODO: i18n.
        pinnable: false,
        minWidth: 170,
        ...additionalColDef,
      };
    });
  });
}

export function buildDatagridAggregationModel(datagridColumns: DatagridElement['columns']): GridAggregationModel {
  return Object.entries(datagridColumns).reduce((acc, [baseDataset, columns]) => {
    columns.forEach((columnName) => {
      const columnType: DatagridColumnTypes | undefined = getColumnType(baseDataset, columnName);
      if (columnType === DatagridColumnTypes.Integer || columnType === DatagridColumnTypes.Currency) {
        const fieldName = buildGridColDefFieldName(baseDataset, columnName);
        acc[fieldName] = 'sum';
      }
    });

    return acc;
  }, {} as GridAggregationModel);
}
